import React, { useState, useCallback } from 'react';
import { ContextualAIModal, ContextSource, AmbientMessageDisplay } from '../components/Space/ContextualAIModal/ContextualAIModal';
import { createFileLogger } from '../utils/logger';
import type { AmbientSuggestion } from './useAmbientSuggestions';

const log = createFileLogger('GlobalCmdK', { enabled: true });

function getSpaceIdFromUrl(): string | null {
  const match = window.location.pathname.match(/\/spaces\/([^/?#]+)/);
  return match?.[1] ?? null;
}

/**
 * Hook for the Contextual AI modal.
 *
 * Modal is opened programmatically (e.g. by Ambient Intelligence Surface via
 * openWithAmbientMessage). There is no keyboard shortcut — use the ambient bubble.
 */
export const useGlobalCmdKShortcut = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [context, setContext] = useState<ContextSource>({ type: 'windows', windowIds: [] });
  const [ambientMessage, setAmbientMessage] = useState<AmbientMessageDisplay | undefined>();
  const [initialPrompt, setInitialPrompt] = useState<string | undefined>();

  // No keyboard shortcut registered — modal is opened programmatically via openWithAmbientMessage.

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setAmbientMessage(undefined);
    setInitialPrompt(undefined);
  }, []);

  /**
   * Open the ContextualAIModal programmatically with a synthesized AI message and suggestion cards.
   * Called by the Ambient Intelligence Surface when the user accepts a suggestion.
   * If no Space is active (no spaceId in URL), the modal cannot submit — logs a warning.
   */
  const openWithAmbientMessage = useCallback((suggestion: AmbientSuggestion) => {
    const spaceId = getSpaceIdFromUrl();
    if (!spaceId) {
      // No Space is active — the submission path requires a Space.tsx listener.
      // TODO: Fall back to opening Integrations settings directly when no Space is open.
      log.warn('[GlobalCmdK] openWithAmbientMessage — no spaceId in URL, cannot open modal');
      return;
    }
    log.info('[GlobalCmdK] openWithAmbientMessage', {
      headline: suggestion.headline,
      cards: suggestion.cards.length,
    });
    setAmbientMessage({ messageText: suggestion.modalMessage, cards: suggestion.cards });
    setInitialPrompt(suggestion.prefilledPrompt);
    setContext(suggestion.context);
    setIsOpen(true);
  }, []);

  const handleSubmit = useCallback((prompt: string, ctx: ContextSource) => {
    const spaceId = getSpaceIdFromUrl();
    if (!spaceId) {
      log.warn('[GlobalCmdK] Submit called but no spaceId in URL — ignoring');
      setIsOpen(false);
      return;
    }

    log.log('[GlobalCmdK] Submitting prompt — spaceId:', spaceId, 'prompt:', prompt.slice(0, 80));

    // Communicate with the Space instance via a DOM event.
    // Space.tsx listens for this event and calls handleContextualAISubmit.
    window.dispatchEvent(
      new CustomEvent('river:contextual-ai-submit', {
        detail: { prompt, context: ctx, spaceId },
      })
    );

    setIsOpen(false);
  }, []);

  const GlobalCmdKModal = isOpen ? (
    <ContextualAIModal
      isOpen={isOpen}
      onClose={handleClose}
      context={context}
      onSubmit={handleSubmit}
      ambientMessage={ambientMessage}
      initialPrompt={initialPrompt}
    />
  ) : null;

  return {
    GlobalCmdKModal,
    openCmdK: () => setIsOpen(true),
    closeCmdK: () => setIsOpen(false),
    isOpen,
    openWithAmbientMessage,
  };
};
